import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useVelaCleanupCounts } from '../../../../hooks/useVelaCleanupCounts';

type StepKind = 'archive' | 'merge' | 'categorize';

interface StepMeta {
  kind: StepKind;
  pillLabel: string;
  titleTemplate: (count: number) => string;
  description: string;
  reviewLabel: string;
  reviewPath: string;
}

const STEP_META: Record<StepKind, StepMeta> = {
  archive: {
    kind: 'archive',
    pillLabel: 'Archive unused',
    titleTemplate: (n) => `Archive ${n} unused account${n !== 1 ? 's' : ''}`,
    description:
      'These accounts have had no activity for over 90 days. Archive to keep your list tidy, you can restore any from Cleanup history for 30 days.',
    reviewLabel: 'Review and archive',
    reviewPath: '/accounting/chart-of-accounts/review-archive',
  },
  merge: {
    kind: 'merge',
    pillLabel: 'Merge duplicates',
    titleTemplate: (n) => `Merge ${n} duplicate pair${n !== 1 ? 's' : ''}`,
    description:
      'These account pairs look like the same thing with slightly different names. Merge to clean up your reports, reversible from Cleanup history for 30 days.',
    reviewLabel: 'Review and merge',
    reviewPath: '/accounting/chart-of-accounts/review-merge',
  },
  categorize: {
    kind: 'categorize',
    pillLabel: 'Categorize uncategorized',
    titleTemplate: (n) => `Categorize ${n} transaction${n !== 1 ? 's' : ''}`,
    description:
      'These transactions are sitting under Uncategorized. Vela picked a category for each one, review and approve to tidy up.',
    reviewLabel: 'Review and categorize',
    reviewPath: '/accounting/chart-of-accounts/review-categorize',
  },
};

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function VelaCleanupModal({ open, onClose }: Props): React.ReactElement | null {
  const navigate = useNavigate();
  const counts = useVelaCleanupCounts();
  const [activeIndex, setActiveIndex] = useState(0);

  const activeSteps = useMemo<StepMeta[]>(() => {
    const out: StepMeta[] = [];
    if (counts.archive > 0) out.push(STEP_META.archive);
    if (counts.merge > 0) out.push(STEP_META.merge);
    if (counts.categorize > 0) out.push(STEP_META.categorize);
    return out;
  }, [counts]);

  useEffect(() => {
    if (activeIndex >= activeSteps.length) {
      setActiveIndex(0);
    }
  }, [activeSteps.length, activeIndex]);

  if (!open) return null;

  const total = counts.total;
  const isEmpty = total === 0;
  const currentStep = activeSteps[activeIndex];

  const goToStep = (idx: number) => setActiveIndex(idx);
  const skipStep = () => {
    if (activeIndex + 1 < activeSteps.length) {
      setActiveIndex(activeIndex + 1);
    } else {
      onClose();
    }
  };
  const reviewCurrent = () => {
    if (!currentStep) return;
    onClose();
    navigate(currentStep.reviewPath);
  };

  const countForKind = (kind: StepKind): number =>
    kind === 'archive' ? counts.archive : kind === 'merge' ? counts.merge : counts.categorize;

  return createPortal(
    <div
      className="fixed inset-0 z-[9000] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-ink/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-card w-full max-w-[580px] overflow-hidden">
        <div className="relative px-5 py-4 border-b border-lineSoft">
          <h2 className="text-[17px] font-extrabold text-ink m-0">
            Clean up your chart of accounts
          </h2>
          <p className="text-[12.5px] text-helper mt-1 m-0">
            {isEmpty
              ? 'Vela checks for unused accounts, duplicates, and uncategorized transactions.'
              : `${total} thing${total !== 1 ? 's' : ''} to review. You approve each one before anything is applied.`}
          </p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-5 w-[26px] h-[26px] rounded-md text-helper bg-transparent border-none cursor-pointer inline-flex items-center justify-center hover:bg-panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isEmpty ? (
          <div className="px-6 pt-9 pb-7 text-center">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-vela to-vela-deep text-white text-[26px] font-extrabold inline-flex items-center justify-center mx-auto mb-3.5 shadow-velaDot">
              V
            </div>
            <h3 className="text-[17px] font-extrabold text-ink m-0 mb-1.5">Everything&apos;s clean</h3>
            <p className="text-small text-helper max-w-[360px] mx-auto mb-[18px] leading-[1.5]">
              No unused accounts, duplicates, or uncategorized transactions right now. Vela will let
              you know when something new needs attention.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="h-[38px] px-5 bg-ink text-white font-bold text-small rounded-lg cursor-pointer border-none"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="px-5 py-5">
            <div className="bg-vela-fade border border-vela-wash rounded-lg p-4">
              <div className="inline-flex items-center gap-2 text-[10.5px] font-extrabold text-vela-deep uppercase tracking-wider mb-2.5">
                <span className="w-[18px] h-[18px] rounded-full bg-gradient-to-br from-vela to-vela-deep text-white text-[10px] font-extrabold inline-flex items-center justify-center">
                  V
                </span>
                Vela suggests
              </div>
              <h3 className="text-[15px] font-extrabold text-ink m-0 mb-1.5">
                {currentStep?.titleTemplate(countForKind(currentStep.kind))}
              </h3>
              <p className="text-[12.5px] text-ink2 m-0 mb-3.5 leading-[1.5]">
                {currentStep?.description}
              </p>
              <div className="flex gap-2 mb-3.5">
                <button
                  type="button"
                  onClick={skipStep}
                  className="h-8 px-3.5 border border-line bg-white text-ink2 text-small font-semibold rounded-md cursor-pointer"
                >
                  Skip
                </button>
                <button
                  type="button"
                  onClick={reviewCurrent}
                  className="h-8 px-3.5 bg-ink text-white text-small font-bold rounded-md cursor-pointer border-none"
                >
                  {currentStep?.reviewLabel}
                </button>
              </div>
              <div className="flex gap-1.5">
                {activeSteps.map((step, idx) => {
                  const isActive = idx === activeIndex;
                  const isDone = idx < activeIndex;
                  return (
                    <button
                      key={step.kind}
                      type="button"
                      onClick={() => goToStep(idx)}
                      className={`px-2.5 py-[5px] rounded-full text-[11px] font-semibold cursor-pointer border-none transition-colors ${
                        isActive
                          ? 'bg-vela-deep text-white'
                          : isDone
                          ? 'bg-white border border-vela-wash text-vela-deep'
                          : 'bg-white border border-line text-helper hover:bg-vela-fade'
                      }`}
                    >
                      {idx + 1}. {step.pillLabel}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center px-5 py-3 border-t border-lineSoft bg-panel text-tiny text-helper">
          {isEmpty ? (
            <>
              <div />
              <div>Vela checked just now</div>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                className="h-8 px-3 border border-line bg-white text-ink2 font-semibold text-small rounded-md cursor-pointer"
              >
                Exit walkthrough
              </button>
              <div>
                Step {activeIndex + 1} of {activeSteps.length}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}